export const APP_THEME = {
  light: 'light',
  dark: 'dark',
  america: 'america',
  professional: 'professional',
} as const

export type AppTheme = (typeof APP_THEME)[keyof typeof APP_THEME]

/** Every theme the app offers, in the order the theme switcher lists them. */
export const APP_THEMES: AppTheme[] = Object.values(APP_THEME)

/** Following the OS instead of naming a theme. */
export const SYSTEM_THEME = 'system'

/** What the user's pick can be: a theme, or leaving it to the OS. */
export type StoredTheme = AppTheme | typeof SYSTEM_THEME

const STORED_THEMES: StoredTheme[] = [SYSTEM_THEME, ...APP_THEMES]

/**
 * The stored pick, if it is still one of ours.
 *
 * `next-themes` hands back whatever it read out of `localStorage`, so until
 * something checks it the pick is a `string`: a theme we have since dropped, or
 * anything else that was written there, arrives looking exactly like a real one.
 * Those come back `undefined`, the same as never having picked.
 */
export const asStoredTheme = (
  value: string | undefined,
): StoredTheme | undefined => STORED_THEMES.find((theme) => theme === value)

/**
 * The theme a page has to be pinned to, given the themes it supports and the
 * one the user picked.
 *
 * Nothing is returned when the page takes every theme, or when the user's pick
 * is already one it supports: pinning and leaving the user alone are different
 * outcomes, since a pinned theme also freezes the theme switcher for as long as
 * the page is mounted.
 *
 * No `userTheme` is the server render and the one that hydrates it, where the
 * pick is not readable. The page's first supported theme stands in, so the
 * pre-paint script has something safe to apply.
 */
export const resolveForcedTheme = (
  supportedThemes: AppTheme[] | undefined,
  userTheme: StoredTheme | undefined,
): AppTheme | undefined => {
  if (!supportedThemes?.length) {
    return undefined
  }
  const isSupported = supportedThemes.some((theme) => theme === userTheme)
  return isSupported ? undefined : supportedThemes[0]
}
